import React from "react";
import VariantGate from './_variant_gate';
import { showToast } from "../utils/show_toast";
import NavBar from "../components/common/nav_bar";
import MaintenanceBanner from "../components/common/maintenance_banner";
import ScrollToTopButton from "../components/common/scroll_to_top_button";
import DelistingNoticesPanel from "../components/exchange_noti_page/delisting_notices_panel";
import NewListingNoticesPanel from "../components/exchange_noti_page/new_listing_notices_panel";
import WalletStatusTable from "../components/exchange_noti_page/wallet_status_table";
import WalletChangeLog from "../components/exchange_noti_page/wallet_change_log";
import WalletStatsCards from "../components/exchange_noti_page/wallet_stats_cards";
import ServiceConnectionIndicator from "../components/exchange_noti_page/service_connection_indicator";
import ExchangeNotiPageV2 from "../components/exchange_noti_page_v2/exchange_noti_page_v2";
import ExchangeNotiPageV3 from "../components/exchange_noti_page_v3/exchange_noti_page_v3";
import { fetchWalletStatus, fetchWalletStatusStats, fetchWalletStatusChanges, fetchNotiHealth } from "../services/exchangeNotiApi";
import { getAccessToken, getStoredUsername, requireLoginOrThrow, clearLoginSession } from "../utils/auth";
import { getNavCollapsed, setNavCollapsed } from "../utils/navState";
import DashboardWebSocket from "../services/websocketClient";
import WalletChangeWebSocket from "../services/walletWebSocketClient";
import { withRouter } from "../utils/withRouter";


class ExchangeNotiPage extends React.Component {
    constructor(props) {
        super(props);
        const sp = props.searchParams;
        this.state = {
            collapsed: getNavCollapsed(),
            walletStatus: [],
            walletStatusLastUpdated: null,
            walletStatusLoading: true,
            walletSearchQuery: "",
            // URL 쿼리에서 복원 (기본값: suspended / true / null / "")
            walletStatusFilter: sp.get("status") || "suspended",
            walletListedOnly: sp.get("listed") !== "false",
            walletMarketFilter: sp.get("market") || null,
            wsStatus: 'disconnected',
            isPollingFallback: false,
            // 통계
            walletStats: null,
            walletStatsLoading: true,
            // 변경 이력
            walletChanges: [],
            walletChangesLoading: true,
            walletChangesPagination: { count: 0, next: null, previous: null },
            walletChangesOffset: 0,
            walletChangesFilterCurrency: sp.get("curr") || "",
            walletChangesRange: sp.get("range") || "7d",
            walletChangesKind: sp.get("kind") || "all",
            // 섹션별 에러 상태
            walletStatusError: false,
            walletStatsError: false,
            walletChangesError: false,
            // 서비스 상태
            serviceHealthStatus: 'loading',
            // 실시간 하이라이트
            walletHighlightIds: [],
        };

        this.dashboardWs = null;
        this.walletPollingInterval = null;
        // DAS-81 — wallet 변경 public 스트림 (인증 없음, 폴링과 병행)
        this.walletChangeWs = null;
    }

    // URL 쿼리 write-through 헬퍼 (기본값은 삭제하여 URL 깨끗하게 유지)
    syncUrl = (patch) => {
        const next = new URLSearchParams(this.props.searchParams);
        const DEFAULTS = {
            status: "suspended",
            listed: "true",
            market: null,
            curr: "",
            range: "7d",
            kind: "all",
        };
        Object.entries(patch).forEach(([k, v]) => {
            const defaultVal = DEFAULTS[k];
            if (v === null || v === undefined || v === "" || String(v) === String(defaultVal)) {
                next.delete(k);
            } else {
                next.set(k, String(v));
            }
        });
        this.props.setSearchParams(next, { replace: true });
    };

    // 기간 preset → [date_from ISO, date_to ISO]
    computeDateRange = (preset) => {
        const now = new Date();
        const endIso = now.toISOString();
        if (preset === "today") {
            const start = new Date(now);
            start.setHours(0, 0, 0, 0);
            return [start.toISOString(), endIso];
        }
        if (preset === "7d") {
            const start = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        if (preset === "30d") {
            const start = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
            return [start.toISOString(), endIso];
        }
        // "all" or unknown
        return [null, null];
    };

    componentDidMount() {
        // eslint-disable-next-line no-restricted-syntax -- componentDidMount only
        requireLoginOrThrow();
        this.connectWebSocket();
        this.connectWalletChangeWs();
        this.fetchStatsData();
        this.fetchChangesData();
        this.fetchServiceHealth();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.searchParams !== this.props.searchParams) {
            const sp = this.props.searchParams;
            const nextStatus = sp.get("status") || "suspended";
            const nextListed = sp.get("listed") !== "false";
            const nextMarket = sp.get("market") || null;
            const nextCurr = sp.get("curr") || "";
            const nextRange = sp.get("range") || "7d";
            const nextKind = sp.get("kind") || "all";
            if (
                nextStatus !== this.state.walletStatusFilter ||
                nextListed !== this.state.walletListedOnly ||
                nextMarket !== this.state.walletMarketFilter ||
                nextCurr !== this.state.walletChangesFilterCurrency ||
                nextRange !== this.state.walletChangesRange ||
                nextKind !== this.state.walletChangesKind
            ) {
                this.setState({
                    walletStatusFilter: nextStatus,
                    walletListedOnly: nextListed,
                    walletMarketFilter: nextMarket,
                    walletChangesFilterCurrency: nextCurr,
                    walletChangesRange: nextRange,
                    walletChangesKind: nextKind,
                    walletChangesOffset: 0,
                }, () => {
                    const changesChanged = (
                        nextCurr !== (prevProps.searchParams.get("curr") || "") ||
                        nextRange !== (prevProps.searchParams.get("range") || "7d") ||
                        nextKind !== (prevProps.searchParams.get("kind") || "all")
                    );
                    if (changesChanged) {
                        this.fetchChangesData();
                    }
                });
            }
        }
    }

    componentWillUnmount() {
        this.stopWalletPolling();
        if (this.dashboardWs) {
            this.dashboardWs.disconnect();
            this.dashboardWs = null;
        }
        if (this.walletChangeWs) {
            this.walletChangeWs.disconnect();
            this.walletChangeWs = null;
        }
    }

    onCollapse = (collapsed) => {
        setNavCollapsed(collapsed);
        this.setState({ collapsed });
    };

    // --- WebSocket + 폴링 폴백 ---

    connectWebSocket = () => {
        const username = getStoredUsername();
        if (!username) {
            this.startWalletPolling();
            return;
        }
        this.dashboardWs = new DashboardWebSocket(
            username,
            getAccessToken,
            this.handleWsMessage,
            this.handleWsStatusChange,
            this.handleWsAuthFailure
        );
        this.dashboardWs.connect();
    };

    // DAS-81 — wallet 변경 public 스트림 구독.
    // 끊겨도 60초 폴링이 fallback 으로 동작하므로 silent 처리.
    connectWalletChangeWs = () => {
        this.walletChangeWs = new WalletChangeWebSocket(this.handleWalletChangeWsMessage);
        this.walletChangeWs.connect();
    };

    handleWalletChangeWsMessage = (message) => {
        if (!message || message.type !== 'wallet_change_update') return;
        // idempotent: 폴링과 중복으로 호출되어도 같은 데이터로 갱신될 뿐 회귀 없음.
        // 신규 row 가 들어왔으니 이력 + 통계 + 상태 즉시 갱신.
        this.fetchChangesData();
        this.fetchStatsData();
        this.fetchWalletStatusData();
    };

    handleWsMessage = (message) => {
        try {
            const { data } = message;
            if (!data) return;
            const { data_type, data: payload } = data;
            if (data_type === 'wallet_status_update') {
                const changes = payload.changes || payload;
                // 변경된 코인의 id 목록 추출
                const changedIds = Array.isArray(changes)
                    ? changes.map(c => `${c.currency}-${c.net_type}`)
                    : [];
                this.setState({
                    walletStatus: Array.isArray(changes) ? changes : this.state.walletStatus,
                    walletStatusLastUpdated: new Date(),
                    walletHighlightIds: changedIds,
                });
                // 3초 후 하이라이트 제거
                setTimeout(() => {
                    this.setState({ walletHighlightIds: [] });
                }, 3000);
                // 통계 + 이력도 새로고침
                this.fetchStatsData();
                this.fetchChangesData();
            }
        } catch (err) {
            console.error('[ExchangeNoti] WebSocket 메시지 처리 실패:', err);
        }
    };

    handleWsAuthFailure = () => {
        try { clearLoginSession(); } catch (e) {}
        window.location.replace("/login");
    };

    handleWsStatusChange = (status) => {
        if (status === 'connected') {
            this.stopWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: false });
            this.fetchWalletStatusData();
        } else if (status === 'disconnected') {
            this.startWalletPolling();
            this.setState({ wsStatus: status, isPollingFallback: true });
        } else {
            this.setState({ wsStatus: status });
        }
    };

    fetchWalletStatusData = () => {
        fetchWalletStatus({ exchange: 'upbit', listed_only: this.state.walletListedOnly })
            .then(res => {
                this.setState({
                    walletStatus: res.data || [],
                    walletStatusLastUpdated: new Date(),
                    walletStatusLoading: false,
                    walletStatusError: false,
                });
            })
            .catch(err => {
                console.error('[ExchangeNoti] Failed to fetch wallet status:', err);
                showToast({ type: "error", message: "지갑 상태 로드에 실패했습니다." });
                this.setState({ walletStatusLoading: false, walletStatusError: true });
            });
    };

    startWalletPolling = () => {
        if (this.walletPollingInterval) return;
        this.fetchWalletStatusData();
        this.walletPollingInterval = setInterval(() => {
            this.fetchWalletStatusData();
        }, 60000);
    };

    stopWalletPolling = () => {
        if (this.walletPollingInterval) {
            clearInterval(this.walletPollingInterval);
            this.walletPollingInterval = null;
        }
    };

    onWalletSearchChange = (e) => {
        this.setState({ walletSearchQuery: e.target.value });
    };

    onWalletStatusFilterChange = (filter) => {
        this.setState({ walletStatusFilter: filter, walletChangesOffset: 0 });
        this.syncUrl({ status: filter });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletListedOnlyChange = (listedOnly) => {
        this.setState({ walletListedOnly: listedOnly, walletChangesOffset: 0 }, () => this.fetchWalletStatusData());
        this.syncUrl({ listed: String(listedOnly) });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onWalletMarketFilterChange = (market) => {
        this.setState({ walletMarketFilter: market, walletChangesOffset: 0 });
        this.syncUrl({ market: market });
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    handleStatsCardClick = (filterKey) => {
        // filterKey: "suspended" (정지 코인 카드)
        // 토글: 현재 이미 해당 필터면 "all" + market=null 로 리셋, 아니면 filterKey 적용
        const currentStatus = this.state.walletStatusFilter;
        const currentMarket = this.state.walletMarketFilter;
        const isActive = currentStatus === filterKey && currentMarket === null;
        if (isActive) {
            this.setState({ walletStatusFilter: "all", walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: "all", market: null });
        } else {
            this.setState({ walletStatusFilter: filterKey, walletMarketFilter: null, walletChangesOffset: 0 });
            this.syncUrl({ status: filterKey, market: null });
        }
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // --- 통계 / 변경 이력 / 서비스 상태 fetch ---

    fetchStatsData = () => {
        this.setState({ walletStatsLoading: true });
        fetchWalletStatusStats()
            .then(res => {
                this.setState({ walletStats: res.data, walletStatsLoading: false, walletStatsError: false });
            })
            .catch(err => {
                console.error('[ExchangeNoti] Failed to fetch wallet stats:', err);
                showToast({ type: "error", message: "지갑 통계 로드에 실패했습니다." });
                this.setState({ walletStatsLoading: false, walletStatsError: true });
            });
    };

    fetchChangesData = () => {
        this.setState({ walletChangesLoading: true });
        const params = { limit: 20, offset: this.state.walletChangesOffset };
        if (this.state.walletChangesFilterCurrency) {
            params.currency = this.state.walletChangesFilterCurrency;
        }
        // 기간 필터 추가
        const [dateFrom, dateTo] = this.computeDateRange(this.state.walletChangesRange);
        if (dateFrom) {
            params.date_from = dateFrom;
        }
        if (dateTo) {
            params.date_to = dateTo;
        }
        if (this.state.walletChangesKind && this.state.walletChangesKind !== "all") {
            params.kind = this.state.walletChangesKind;
        }
        fetchWalletStatusChanges(params)
            .then(res => {
                this.setState({
                    walletChanges: res.data.results || [],
                    walletChangesPagination: {
                        count: res.data.count || 0,
                        next: res.data.next,
                        previous: res.data.previous,
                    },
                    walletChangesLoading: false,
                    walletChangesError: false,
                });
            })
            .catch(err => {
                console.error('[ExchangeNoti] Failed to fetch wallet changes:', err);
                showToast({ type: "error", message: "변경 이력 로드에 실패했습니다." });
                this.setState({ walletChangesLoading: false, walletChangesError: true });
            });
    };

    fetchServiceHealth = () => {
        this.setState({ serviceHealthStatus: 'loading' });
        fetchNotiHealth()
            .then(res => {
                const st = (res.data && res.data.status === 'ok') ? 'ok' : 'error';
                this.setState({ serviceHealthStatus: st });
            })
            .catch(() => {
                this.setState({ serviceHealthStatus: 'error' });
            });
    };

    onChangesPageChange = (direction) => {
        this.setState(prevState => {
            const newOffset = direction === 'next'
                ? prevState.walletChangesOffset + 20
                : Math.max(0, prevState.walletChangesOffset - 20);
            return { walletChangesOffset: newOffset };
        }, () => this.fetchChangesData());
    };

    onChangesFilterCurrencyChange = (value) => {
        this.setState({ walletChangesFilterCurrency: value, walletChangesOffset: 0 }, () => this.fetchChangesData());
        this.syncUrl({ curr: value });
    };

    onWalletChangesRangeChange = (range) => {
        this.setState(
            { walletChangesRange: range, walletChangesOffset: 0 },
            () => this.fetchChangesData()
        );
        this.syncUrl({ range });
    };

    onWalletChangesKindChange = (kind) => {
        this.setState(
            { walletChangesKind: kind, walletChangesOffset: 0 },
            () => this.fetchChangesData()
        );
        this.syncUrl({ kind });
    };

    render() {
        const { collapsed } = this.state;

        // ServiceConnectionIndicator 상태 결합: WebSocket + 서비스 헬스
        let combinedServiceStatus = this.state.serviceHealthStatus;
        if (this.state.serviceHealthStatus === 'ok' && this.state.wsStatus === 'disconnected' && this.state.isPollingFallback) {
            combinedServiceStatus = 'degraded';
        } else if (this.state.serviceHealthStatus === 'error') {
            combinedServiceStatus = 'error';
        }

        return (
            <div className="min-h-screen w-full overflow-x-hidden bg-surface text-content sm:overflow-x-auto">
                <NavBar collapsed={collapsed} onCollapse={this.onCollapse} />
                <main
                    className={`min-w-0 w-full pt-12 pb-16 sm:pt-0 sm:pb-0 transition-[padding-left] duration-200 ${collapsed ? "sm:pl-[56px]" : "sm:pl-[220px]"}`}
                    style={{ marginTop: "var(--maintenance-banner-h, 0px)" }}
                >
                    <MaintenanceBanner />
                    <div className="mx-3 my-3 min-w-0 rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card sm:mx-6 sm:my-5 sm:p-6">
                        {/* 헤더 + 검색 input (헤더 영역으로 이동) */}
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="flex items-center gap-3">
                                    <div className="text-sm text-content-secondary">거래소 / 지갑 상태</div>
                                    <ServiceConnectionIndicator status={combinedServiceStatus} />
                                </div>
                                <h1 className="mt-1 text-xl font-semibold text-content">지갑 입출금 현황</h1>
                            </div>
                        </div>

                        {/* 지갑 상태 통계 카드 */}
                        <div className="mt-6">
                            <WalletStatsCards
                                stats={this.state.walletStats}
                                loading={this.state.walletStatsLoading}
                                error={this.state.walletStatsError}
                                onCardClick={this.handleStatsCardClick}
                                activeFilterKey={
                                    this.state.walletStatusFilter === "suspended" && this.state.walletMarketFilter === null
                                        ? "suspended"
                                        : null
                                }
                            />
                        </div>

                        {/* 신규 상장 공지 */}
                        <div className="mt-6">
                            <NewListingNoticesPanel />
                        </div>

                        {/* 거래지원 종료 예정 */}
                        <div className="mt-6">
                            <DelistingNoticesPanel />
                        </div>

                        {/* 지갑 입출금 상태 테이블 */}
                        <div className="mt-6">
                            <h2 className="text-base font-semibold text-content mb-3">지갑 입출금 상태</h2>
                            <WalletStatusTable
                                data={this.state.walletStatus}
                                loading={this.state.walletStatusLoading}
                                lastUpdated={this.state.walletStatusLastUpdated}
                                searchQuery={this.state.walletSearchQuery}
                                statusFilter={this.state.walletStatusFilter}
                                onSearchChange={this.onWalletSearchChange}
                                onStatusFilterChange={this.onWalletStatusFilterChange}
                                highlightIds={this.state.walletHighlightIds}
                                error={this.state.walletStatusError}
                                listedOnly={this.state.walletListedOnly}
                                onListedOnlyChange={this.onWalletListedOnlyChange}
                                marketFilter={this.state.walletMarketFilter}
                                onMarketFilterChange={this.onWalletMarketFilterChange}
                            />
                        </div>

                        {/* 지갑 상태 변경 이력 */}
                        <div className="mt-6">
                            <h2 className="text-base font-semibold text-content mb-3">상태 변경 이력</h2>
                            <WalletChangeLog
                                changes={this.state.walletChanges}
                                loading={this.state.walletChangesLoading}
                                pagination={this.state.walletChangesPagination}
                                onPageChange={this.onChangesPageChange}
                                filterCurrency={this.state.walletChangesFilterCurrency}
                                onFilterCurrencyChange={this.onChangesFilterCurrencyChange}
                                error={this.state.walletChangesError}
                                range={this.state.walletChangesRange}
                                onRangeChange={this.onWalletChangesRangeChange}
                                kind={this.state.walletChangesKind}
                                onKindChange={this.onWalletChangesKindChange}
                            />
                        </div>
                    </div>
                </main>
                <ScrollToTopButton />
            </div>
        );
    }
}

// DAS-70 Wave-A: variant 슬롯 3-단 확장 (v1 / v2 슬롯 / v3 정보 구조 재설계)
const ExchangeNotiPageV1 = withRouter(ExchangeNotiPage);
export { ExchangeNotiPageV1 };
const ExchangeNotiPageWithVariant = (props) => (
    <VariantGate v1={ExchangeNotiPageV1} v2={ExchangeNotiPageV2} v3={ExchangeNotiPageV3} fallbackToV1={true} {...props} />
);
export default ExchangeNotiPageWithVariant;
